import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { authService } from './services/authService';
import { bootstrapAccessToken, clearAuthStorage } from './services/api';
import notificationService from './services/notificationService';
import { pageAccessRules } from './hooks/useRBAC';
import { FeatureCode } from './types';
import { WalkthroughProvider } from './components/help/WalkthroughProvider';
import WalkthroughOverlay from './components/help/WalkthroughOverlay';
import OnboardingModal, { isOnboardingComplete } from './components/help/OnboardingModal';
import ContextHelpDrawer from './components/help/ContextHelpDrawer';
import ErrorBoundary from './components/common/ErrorBoundary';
import { PAGE_TO_PATH, PATH_TO_PAGE } from './constants/routes';
import {
  setEffectiveFeatures as syncFeatureFlagsStore,
  setPlanFeatures,
  hasModuleAccess,
  getUserPermissions,
  clearUserPermissions,
} from './utils/featureFlagsStore';

const CHUNK_RELOAD_KEY = 'app_chunk_reload';

function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const module = await factory();
        if (!module?.default) {
          throw new Error('Lazy-loaded module is missing a default export');
        }
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return module;
      } catch (err) {
        lastError = err;
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    }

    if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return new Promise(() => {});
    }

    throw lastError;
  });
}

// Lazy-loaded page components (cast to any to avoid prop-type mismatch with onNavigate signatures)
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard/Dashboard')) as React.LazyExoticComponent<React.FC<any>>;
const GstLanding = lazyWithRetry(() => import('./pages/Gst/GstLanding')) as React.LazyExoticComponent<React.FC<any>>;
const InventoryLanding = lazyWithRetry(() => import('./pages/Inventory/InventoryLanding')) as React.LazyExoticComponent<React.FC<any>>;
const OrdersLanding = lazyWithRetry(() => import('./pages/Orders/OrdersLanding')) as React.LazyExoticComponent<React.FC<any>>;
const WorkforceLanding = lazyWithRetry(() => import('./pages/Workforce/WorkforceLanding')) as React.LazyExoticComponent<React.FC<any>>;
const AssetsLanding = lazyWithRetry(() => import('./pages/Assets/AssetsLanding')) as React.LazyExoticComponent<React.FC<any>>;
const FinanceLanding = lazyWithRetry(() => import('./pages/Finance/FinanceLanding')) as React.LazyExoticComponent<React.FC<any>>;
const ApprovalsLanding = lazyWithRetry(() => import('./pages/Approvals/ApprovalsLanding')) as React.LazyExoticComponent<React.FC<any>>;
const NotificationsLanding = lazyWithRetry(() => import('./pages/Notifications/NotificationsLanding')) as React.LazyExoticComponent<React.FC<any>>;
const ReportsLanding = lazyWithRetry(() => import('./pages/Reports/ReportsLanding')) as React.LazyExoticComponent<React.FC<any>>;
const FactoryLanding = lazyWithRetry(() => import('./pages/Factory/FactoryLanding')) as React.LazyExoticComponent<React.FC<any>>;
const Login = lazyWithRetry(() => import('./pages/Login/Login')) as React.LazyExoticComponent<React.FC<any>>;
const Profile = lazyWithRetry(() => import('./pages/Profile/Profile')) as React.LazyExoticComponent<React.FC<any>>;
const SettingsPage = lazyWithRetry(() => import('./pages/Settings/SettingsPage')) as React.LazyExoticComponent<React.FC<any>>;
const HelpCenterPage = lazyWithRetry(() => import('./pages/Help/HelpCenterPage')) as React.LazyExoticComponent<React.FC<any>>;

// Loading spinner shown during lazy-load suspense
const PageLoader: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#f9fafb',
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f4f6',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }} />
    <style>
      {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
    </style>
  </div>
);

// ─── Auth guard — redirect unauthenticated users to login ─────
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// Module → permission code mapping for RBAC-based route gating
const PAGE_PERMISSION_MAP: Partial<Record<string, string>> = {
  gst:           'gst_compliance.view',
  inventory:     'inventory.view',
  factory:       'production.view',
  orders:        'orders.view',
  workforce:     'workforce.view',
  assets:        'assets.view',
  finance:       'finance.view',
  reports:       'reports.view',
  approvals:     'approvals.view',
  notifications: 'notifications.view',
};

// ─── Access guard — RBAC-aware ────────────────────────────────
const AccessGuardedRoute: React.FC<{
  pageName: 'dashboard' | 'gst' | 'inventory' | 'factory' | 'orders' | 'workforce' | 'assets' | 'finance' | 'reports' | 'approvals' | 'notifications' | 'settings';
  children: React.ReactNode;
  onLogout: () => void;
}> = ({ pageName, children, onLogout }) => {
  // Not authenticated at all → go to login, not "access denied"
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const user          = authService.getCurrentUser();
  const rbacLoaded    = getUserPermissions().length > 0;
  const permCode      = PAGE_PERMISSION_MAP[pageName];
  const allowedRoles  = pageAccessRules[pageName] || [];

  let hasAccess: boolean;
  if (rbacLoaded && permCode) {
    // Fine-grained RBAC check: user must have the module's .view permission
    hasAccess = hasModuleAccess(permCode.split('.')[0]);
  } else {
    // Fallback: coarse role-based check (backward compat for users without assigned_role)
    hasAccess = Boolean(user && allowedRoles.length > 0 && allowedRoles.includes(user.role));
  }

  // Settings page: always accessible to Owner and Manager regardless of RBAC
  if (pageName === 'settings') {
    hasAccess = Boolean(user && (user.role === 'Owner' || user.role === 'Manager'));
  }

  if (!hasAccess) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f9fafb',
        flexDirection: 'column',
        gap: '1rem',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#111827', marginBottom: '0.5rem' }}>Access Denied</h1>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            You don't have permission to access this page.
          </p>
          {user && (
            <p style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
              Your role: {user.role || 'Not set'}
            </p>
          )}
          <div style={{ marginTop: '0.75rem' }}>
            <button
              onClick={() => onLogout()}
              style={{
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Log out / Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const DEFAULT_EFFECTIVE_FEATURES: Record<string, boolean> = {};

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionBootstrapError, setSessionBootstrapError] = useState<string | null>(null);
  const [effectiveFeatures, setEffectiveFeatures] = useState<Record<string, boolean>>(DEFAULT_EFFECTIVE_FEATURES);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showContextHelp, setShowContextHelp] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const prevUnreadCountRef = useRef(0);
  const [newNotificationAlert, setNewNotificationAlert] = useState<string | null>(null);
  const newNotificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const inventoryEnabled = Boolean(effectiveFeatures[FeatureCode.INVENTORY]);
  const gstComplianceEnabled = Boolean(effectiveFeatures[FeatureCode.GST_COMPLIANCE]);
  const ordersEnabled = Boolean(effectiveFeatures[FeatureCode.ORDERS]);
  const assetsEnabled = Boolean(effectiveFeatures[FeatureCode.ASSETS]);
  const financeEnabled = Boolean(effectiveFeatures[FeatureCode.FINANCE]);
  const workforceEnabled = Boolean(effectiveFeatures[FeatureCode.WORKFORCE]);
  const productionEnabled = Boolean(effectiveFeatures[FeatureCode.PRODUCTION]);
  const processingEnabled = Boolean(effectiveFeatures[FeatureCode.PROCESSING]);
  const qualityEnabled = Boolean(effectiveFeatures[FeatureCode.QUALITY]);
  const factoryEnabled = productionEnabled || processingEnabled || qualityEnabled;
  const approvalsEnabled = Boolean(effectiveFeatures[FeatureCode.APPROVALS]);
  const notificationsEnabled = Boolean(effectiveFeatures[FeatureCode.NOTIFICATIONS]);
  const reportsEnabled = Boolean(effectiveFeatures[FeatureCode.REPORTS]);
  const wastageEnabled = Boolean(effectiveFeatures[FeatureCode.WASTAGE]);

  // Bridge function: translates old page-name strings to router navigation
  const onNavigate = useCallback((page: string) => {
    const path = PAGE_TO_PATH[page];
    if (path) {
      navigate(path);
    }
  }, [navigate]);

  // Derive current "page name" from the URL path for Sidebar active state and context help
  const currentPage = PATH_TO_PAGE[location.pathname] || 'dashboard';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';

  // Fetch effective feature flags (resolves plan defaults + tenant overrides).
  // This is the SINGLE place this API is called — child pages must not call it again.
  const fetchEffectiveFeatures = useCallback(async () => {
    try {
      const result = await notificationService.getEffectiveFeatures();
      if (result?.effective_features) {
        setEffectiveFeatures(result.effective_features);
        syncFeatureFlagsStore(result.effective_features);
      }
      if (result?.plan_features) {
        setPlanFeatures(result.plan_features);
      }
    } catch {
      // Keep current defaults on error — safe fallback keeps all modules accessible
    }
  }, []);

  // Fetch unread count — show a banner when new notifications arrive
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationService.getUnreadCount();
      const newCount = typeof data.unread_count === 'number' ? data.unread_count : 0;
      if (newCount > prevUnreadCountRef.current && prevUnreadCountRef.current >= 0) {
        setNewNotificationAlert('You have new notifications');
        if (newNotificationTimerRef.current) clearTimeout(newNotificationTimerRef.current);
        newNotificationTimerRef.current = setTimeout(() => setNewNotificationAlert(null), 5000);
      }
      prevUnreadCountRef.current = newCount;
      setUnreadNotificationCount(newCount);
    } catch {
      // silent
    }
  }, []);

  // Start/stop polling when auth or notification feature changes
  useEffect(() => {
    if (!isAuthPage && notificationsEnabled) {
      prevUnreadCountRef.current = -1;
      fetchUnreadCount();
      pollingRef.current = setInterval(fetchUnreadCount, 30000);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isAuthPage, notificationsEnabled, fetchUnreadCount]);

  // Bootstrap session: silent refresh before any protected API calls
  useEffect(() => {
    const init = async () => {
      const mayHaveSession = localStorage.getItem('logged_in') === 'true';
      if (mayHaveSession) {
        const result = await bootstrapAccessToken();
        if (result.status === 'auth_failed') {
          clearAuthStorage();
          await authService.logout().catch(() => {});
          if (!isAuthPage) {
            navigate('/login', { replace: true });
          }
          setIsInitialized(true);
          return;
        }
        if (result.status === 'transient_failed') {
          setSessionBootstrapError('Unable to restore your session. Check your connection and retry.');
          setIsInitialized(true);
          return;
        }
        setSessionBootstrapError(null);
        await Promise.all([
          fetchEffectiveFeatures(),
          authService.getUserDetails().catch(() => null),
        ]);
        if (isAuthPage) {
          navigate('/dashboard', { replace: true });
        }
      } else if (!isAuthPage) {
        navigate('/login', { replace: true });
      }
      setIsInitialized(true);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrySessionBootstrap = useCallback(async () => {
    setSessionBootstrapError(null);
    setIsInitialized(false);
    const result = await bootstrapAccessToken();
    if (result.status === 'auth_failed') {
      clearAuthStorage();
      await authService.logout().catch(() => {});
      navigate('/login', { replace: true });
      setIsInitialized(true);
      return;
    }
    if (result.status === 'transient_failed') {
      setSessionBootstrapError('Still unable to restore your session. Please try again.');
      setIsInitialized(true);
      return;
    }
    setSessionBootstrapError(null);
    await Promise.all([
      fetchEffectiveFeatures(),
      authService.getUserDetails().catch(() => null),
    ]);
    setIsInitialized(true);
  }, [fetchEffectiveFeatures, navigate]);

  if (!isInitialized) {
    return <PageLoader />;
  }

  if (sessionBootstrapError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f9fafb',
        padding: '1.5rem',
        textAlign: 'center',
        gap: '1rem',
      }}>
        <p style={{ color: '#374151', margin: 0, maxWidth: '420px' }}>{sessionBootstrapError}</p>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => { void retrySessionBootstrap(); }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => {
              clearAuthStorage();
              void authService.logout().catch(() => {});
              navigate('/login', { replace: true });
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  const handleLoginSuccess = async () => {
    await fetchEffectiveFeatures();
    navigate('/dashboard', { replace: true });
    if (!isOnboardingComplete()) {
      setShowOnboarding(true);
    }
  };

  const handleLogout = async () => {
    // Clear local auth state first so any re-render triggered by navigate()
    // finds isAuthenticated() === false and goes to /login, not "Access Denied".
    await authService.logout();
    setEffectiveFeatures(DEFAULT_EFFECTIVE_FEATURES);
    syncFeatureFlagsStore(DEFAULT_EFFECTIVE_FEATURES);
    clearUserPermissions();
    setUnreadNotificationCount(0);
    // Use window.location.replace so the page fully reloads to a clean state.
    window.location.replace('/login');
  };

  // Common notification & feature props for all page components
  const commonFeatureProps = {
    notificationCount: unreadNotificationCount,
    notificationsEnabled,
    approvalsEnabled,
    gstComplianceEnabled,
    inventoryEnabled,
    ordersEnabled,
    assetsEnabled,
    financeEnabled,
    workforceEnabled,
    productionEnabled,
    processingEnabled,
    reportsEnabled,
    wastageEnabled,
    qualityEnabled,
    onNotificationCountChange: fetchUnreadCount,
    onHelpClick: () => setShowContextHelp(true),
  };

  // Get page name for context help (exclude login/signup/profile/help)
  const contextHelpPage = ['dashboard', 'gst', 'inventory', 'factory', 'orders', 'workforce', 'assets', 'finance', 'reports', 'approvals', 'notifications'].includes(currentPage) ? currentPage : null;

  // Helper to render a guarded page
  const guarded = (
    pageName: 'dashboard' | 'gst' | 'inventory' | 'factory' | 'orders' | 'workforce' | 'assets' | 'finance' | 'reports' | 'approvals' | 'notifications' | 'settings',
    element: React.ReactNode,
  ) => (
    <AccessGuardedRoute pageName={pageName} onLogout={handleLogout}>
      {element}
    </AccessGuardedRoute>
  );

  return (
    <WalkthroughProvider>
      <WalkthroughOverlay />
      {/* Onboarding modal */}
      {showOnboarding && (
        <OnboardingModal onComplete={() => setShowOnboarding(false)} onNavigate={onNavigate} />
      )}
      {/* Context Help Drawer */}
      {contextHelpPage && (
        <ContextHelpDrawer
          pageName={contextHelpPage}
          open={showContextHelp}
          onClose={() => setShowContextHelp(false)}
          onNavigateToHelp={() => navigate('/help')}
          onNavigateToArticle={(articleId, category) => {
            setShowContextHelp(false);
            navigate(`/help?article=${encodeURIComponent(articleId)}&category=${encodeURIComponent(category)}`);
          }}
          enabledFeatures={{
            INVENTORY: inventoryEnabled,
            ORDERS: ordersEnabled,
            PRODUCTION: productionEnabled,
            PROCESSING: processingEnabled,
            WASTAGE: wastageEnabled,
            REPORTS: reportsEnabled,
            WORKFORCE: workforceEnabled,
            ASSETS: assetsEnabled,
            FINANCE: financeEnabled,
            APPROVALS: approvalsEnabled,
            NOTIFICATIONS: notificationsEnabled,
          }}
        />
      )}
      {/* New-notification banner — appears when unread count increases */}
      {newNotificationAlert && (
        <div
          onClick={() => { setNewNotificationAlert(null); navigate('/notifications'); }}
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 9999,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '0.75rem 1.25rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            animation: 'slideIn 0.3s ease',
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>🔔</span>
          {newNotificationAlert}
          <span style={{ marginLeft: '0.5rem', opacity: 0.6, fontSize: '0.75rem' }}>Click to view</span>
        </div>
      )}
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              authService.isAuthenticated()
                ? <Navigate to="/dashboard" replace />
                : <Login onLoginSuccess={handleLoginSuccess} />
            } />
            <Route path="/signup" element={<Navigate to="/login" replace />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              guarded('dashboard', <Dashboard onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
            } />
            <Route path="/gst" element={
              gstComplianceEnabled
                ? guarded('gst', <GstLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/inventory" element={
              inventoryEnabled
                ? guarded('inventory', <InventoryLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/factory" element={
              factoryEnabled
                ? guarded('factory', <FactoryLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/orders" element={
              ordersEnabled
                ? guarded('orders', <OrdersLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/workforce" element={
              workforceEnabled
                ? guarded('workforce', <WorkforceLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/assets" element={
              assetsEnabled
                ? guarded('assets', <AssetsLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/finance" element={
              financeEnabled
                ? guarded('finance', <FinanceLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/reports" element={
              reportsEnabled
                ? guarded('reports', <ReportsLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/quality" element={
              qualityEnabled
                ? <Navigate to="/factory?tab=quality_checks" replace />
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/approvals" element={
              approvalsEnabled
                ? guarded('approvals', <ApprovalsLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
                : <Navigate to="/dashboard" replace />
            } />
            <Route path="/notifications" element={
              guarded('notifications', <NotificationsLanding onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />)
            } />
            <Route path="/profile" element={
              <RequireAuth>
                <Profile onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} onFeatureFlagsChange={fetchEffectiveFeatures} />
              </RequireAuth>
            } />
            <Route path="/settings" element={
              guarded('settings', <SettingsPage onNavigate={onNavigate} onLogout={handleLogout} onFeatureFlagsChange={fetchEffectiveFeatures} {...commonFeatureProps} />)
            } />
            <Route path="/help" element={
              <RequireAuth>
                <HelpCenterPage onNavigate={onNavigate} onLogout={handleLogout} {...commonFeatureProps} />
              </RequireAuth>
            } />

            {/* Default: redirect to dashboard (or login if unauthenticated) */}
            <Route path="*" element={
              <Navigate to={authService.isAuthenticated() ? '/dashboard' : '/login'} replace />
            } />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </WalkthroughProvider>
  );
};

export default App;